import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import QRCode from 'qrcode';
import { api, ApiError, dateAr, clock, STATUS_AR, type PlanView, type ShopPublic } from '../lib/api';
import { TopBar, Notice, Spinner } from '../components/ui';
import { Wordmark } from '../components/Logo';
import { OtpStep } from '../components/Otp';
import { WalletButtons } from '../components/WalletButtons';

type CardData = {
  member: { name: string; member_no: string; phone_last4: string; marketing_opt_in: boolean };
  shop: ShopPublic;
  plan?: PlanView;
  payments_mode: 'live' | 'mock' | 'counter';
  subscription: null | {
    id: string; status: string; cycle_start: string | null; cycle_end: string | null; visits_total: number; visits_left: number; used: number;
    last_use_at: string | null; can_redeem_now: boolean; deny_reason: string | null; withdrawal: { eligible: boolean; refund_halalas: number }; cancel_requested_at: string | null;
    renew_open: boolean; renewal_mode: 'auto' | 'link'; paid_method: 'card' | 'cash' | 'mock' | null;
  };
  visits?: { id: string; kind: 'use' | 'reversal'; at: string; local_time: string }[];
};

export default function Card() {
  const { token = '' } = useParams();
  const [sp] = useSearchParams();
  const [data, setData] = useState<CardData | null>(null);
  const [error, setError] = useState('');
  const [qr, setQr] = useState('');
  const [confirm, setConfirm] = useState<null | 'period_end' | 'withdraw'>(null);
  const [verification, setVerification] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');

  const load = () => api.get<CardData>(`/api/public/cards/${token}`).then(setData).catch(() => setError('البطاقة غير موجودة.'));
  useEffect(() => { load(); }, [token]);
  useEffect(() => { QRCode.toDataURL(`${location.origin}/card/${token}`, { margin: 1, width: 240, color: { dark: '#000000', light: '#FFFFFF' } }).then(setQr); }, [token]);

  async function cancel(mode: 'period_end' | 'withdraw') {
    setBusy(true);
    try {
      const r = await api.post<{ ok: boolean; mode: string; until?: string; refund_halalas?: number; refund_by?: string }>(`/api/public/cards/${token}/cancel`, { mode, verification_token: verification });
      setDone(mode === 'withdraw'
        ? `تم العدول. المبلغ المسترد ${((r.refund_halalas ?? 0) / 100).toFixed(2)} ريال ${r.refund_by === 'shop' ? 'يُرد لك من المحل مباشرة.' : 'يعود إلى وسيلة الدفع نفسها.'}`
        : `تم استلام طلب الإلغاء. تبقى عضويتك فعّالة حتى ${dateAr(r.until)}.`);
      setConfirm(null); setVerification(''); await load();
    } catch (e) { setDone(e instanceof ApiError ? (e.message_ar ?? e.message) : 'تعذر تنفيذ الطلب.'); }
    setBusy(false);
  }
  async function renew() {
    setBusy(true);
    try {
      const r = await api.post<{ ok: boolean; done: boolean; payment?: Record<string, unknown> }>(`/api/public/cards/${token}/renew`, {});
      if (r.done) { setDone('تم التجديد. حلاقاتك الجديدة أُضيفت إلى بطاقتك.'); await load(); }
      else setDone('سيُفتح الدفع عبر بوابة المحل.');
    } catch (e) { setDone(e instanceof ApiError ? (e.message_ar ?? e.message) : 'تعذر التجديد.'); }
    setBusy(false);
  }
  async function sendDispute(redId: string) {
    const note = window.prompt('ما الذي حدث؟ (يصل لصاحب المحل)');
    if (!note) return;
    try { await api.post(`/api/public/cards/${token}/dispute`, { redemption_id: redId, note }); setDone('وصل اعتراضك لصاحب المحل.'); }
    catch { setDone('تعذر إرسال الاعتراض.'); }
  }

  if (error) return <main className="container py-10"><TopBar /><Notice kind="danger">{error}</Notice></main>;
  if (!data) return <main className="container py-10"><TopBar /><Spinner label="نفتح بطاقتك" /></main>;
  const s = data.subscription;
  const st = s ? (STATUS_AR[s.status] ?? s.status) : null;
  const stKind = s ? (s.status === 'active' ? 'ok' : s.status === 'cancel_at_period_end' || s.status === 'past_due' ? 'warn' : 'danger') : 'ok';

  return (
    <main className="min-h-dvh pb-16">
      <TopBar right={<Link to={`/s/${data.shop.slug}`} className="text-sm underline">{data.shop.name}</Link>} />
      <div className="container max-w-xl grid gap-4">
        {sp.get('new') && <Notice kind="ok">عضويتك فعّالة. أرسلنا بطاقتك على واتساب أيضًا. احفظ هذا الرابط.</Notice>}
        {sp.get('reserved') && <Notice kind="ok">تم حجز عضويتك وأرسلنا بطاقتك على واتساب. ادفع عند الحلاق في أول زيارة وتُفعَّل فورًا.</Notice>}
        {s && s.status === 'pending' && data.plan && (
          <section className="rounded-[var(--radius-lg)] bg-accent text-ink p-6 grid gap-2">
            <div className="eyebrow">عند أول زيارة</div>
            <h2 style={{ fontSize: '1.6rem' }}>ادفع {data.plan.price_label} للحلاق، وقل له رقم عضويتك</h2>
            <div className="display num" style={{ fontSize: '3rem' }}>{data.member.member_no}</div>
            <p className="text-sm">يسجّل الدفع وأول حلاقة بزر واحد، وتتحول بطاقتك إلى فعّالة في اللحظة نفسها.</p>
          </section>
        )}
        {s && s.status !== 'pending' && data.payments_mode === 'counter' && data.plan && (s.status === 'past_due' || (s.cycle_end && new Date(s.cycle_end).getTime() - Date.now() < 7 * 86_400_000)) && (
          <Notice kind="info">{s.status === 'past_due' ? 'انتهت دورتك.' : 'دورتك تنتهي قريبًا.'} التجديد عند الحلاق: ادفع {data.plan.price_label} في زيارتك القادمة ويسجّله لك.</Notice>
        )}
        {done && <Notice kind="info">{done}</Notice>}

        <section className="rounded-[var(--radius-lg)] bg-ink text-paper p-6 relative overflow-hidden">
          <div className="absolute -start-4 -bottom-6 opacity-10 rotate-[-6deg]"><Wordmark height={110} tone="paper" /></div>
          <div className="relative">
            <div className="flex items-center justify-between"><span className="eyebrow">عضوية {data.shop.name}</span>{st && <span className={`pill pill-${stKind}`}>{st}</span>}</div>
            <div className="mt-3 text-paper/70 text-sm">{data.shop.district ?? ''}{data.shop.cr_number ? ` · س.ت ${data.shop.cr_number}` : ''}</div>
            <div className="display mt-1" style={{ fontSize: '1.9rem' }}>{data.member.name}</div>
            <div className="num text-paper/80">رقم العضوية {data.member.member_no}</div>
            {s && data.plan && (
              <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-[var(--radius-md)] bg-paper/10 p-3"><div className="display num" style={{ fontSize: '2rem' }}>{s.visits_left}</div><div className="text-xs text-paper/70">المتبقي من حلاقاتك</div></div>
                <div className="rounded-[var(--radius-md)] bg-paper/10 p-3"><div className="display num" style={{ fontSize: '2rem' }}>{s.used}</div><div className="text-xs text-paper/70">مستخدمة</div></div>
                <div className="rounded-[var(--radius-md)] bg-paper/10 p-3"><div className="font-bold num text-sm mt-2">{dateAr(s.cycle_end)}</div><div className="text-xs text-paper/70">{s.renewal_mode === 'auto' ? 'التجديد' : 'نهاية الدورة'}</div></div>
              </div>
            )}
            {data.plan && <div className="mt-4 text-sm text-paper/80">{data.plan.name} · {data.plan.price_label}{data.plan.window ? ` · من ${clock(data.plan.window.start_min)} إلى ${clock(data.plan.window.end_min)}` : ''}</div>}
            <div className="mt-3 text-[11px] text-paper/50 flex items-center gap-1">تشغيل تقني <Wordmark height={10} tone="paper" opacity={0.8} /></div>
          </div>
        </section>

        {s && <WalletButtons token={token} />}
        {s && s.renew_open && (
          <section className="card p-5 grid gap-3 border-accent">
            <h3>{s.status === 'past_due' ? 'انتهت دورتك. جدّد لتبقى حلاقاتك المتبقية.' : 'دورتك تنتهي قريبًا'}</h3>
            <p className="muted text-sm">{data.plan?.price_label}. لا يُسحب أي مبلغ إلا بضغطتك هنا{s.paid_method === 'cash' ? '، أو ادفع في المحل ويسجّلها صاحب المحل' : ''}.</p>
            <button className="btn btn-accent btn-lg" disabled={busy} onClick={renew}>{busy ? 'لحظة...' : 'جدّد الآن'}</button>
          </section>
        )}

        {s && (
          <section className="card p-5 grid gap-3">
            <div className="flex items-center gap-4">
              {qr && <img src={qr} alt="رمز البطاقة" width={120} height={120} className="rounded-md border border-line" />}
              <div>
                <h3>عند الحلاق</h3>
                <p className="muted text-sm mt-1">قل له رقم عضويتك <b className="num">{data.member.member_no}</b> أو آخر 4 أرقام من جوالك. يسجل الزيارة بزر واحد وتنقص من حلاقاتك فورًا.</p>
                <div className="mt-2">{s.can_redeem_now ? <span className="pill pill-ok">يمكنك الزيارة الآن</span> : <span className="pill">{s.deny_reason}</span>}</div>
              </div>
            </div>
          </section>
        )}

        <section className="card p-5 flex items-center justify-between gap-4">
          <div>
            <h3>عروض {data.shop.name} على واتساب</h3>
            <p className="muted text-sm mt-1">{data.member.marketing_opt_in ? 'تصلك رسائل المحل الترويجية. أوقفها هنا أو بإرسال كلمة «إيقاف» على واتساب.' : 'لا تصلك رسائل ترويجية من المحل. رسائل عضويتك (البطاقة والزيارات والتجديد) تصلك دائمًا.'}</p>
          </div>
          <button className="btn btn-ghost shrink-0" disabled={busy} onClick={async () => { setBusy(true); try { await api.post(`/api/public/cards/${token}/marketing`, { opt_in: !data.member.marketing_opt_in }); await load(); } finally { setBusy(false); } }}>{data.member.marketing_opt_in ? 'إيقاف' : 'تفعيل'}</button>
        </section>

        {data.visits && data.visits.length > 0 && (
          <section className="card p-5">
            <h3>سجل الزيارات</h3>
            <ul className="mt-2 divide-y divide-line">
              {data.visits.slice().reverse().map((v) => (
                <li key={v.id} className="py-2 flex justify-between items-center text-sm">
                  <span>{v.kind === 'use' ? 'زيارة' : 'تراجع عن زيارة'}</span>
                  <span className="flex items-center gap-3"><span className="num muted">{v.local_time}</span>{v.kind === 'use' && <button className="text-xs underline" onClick={() => sendDispute(v.id)}>ليست زيارتي</button>}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {s && ['active', 'cancel_at_period_end', 'past_due'].includes(s.status) && (
          <section className="card p-5 grid gap-3">
            <h3>إدارة العضوية</h3>
            {s.status === 'active' && !confirm && (
              <div className="flex flex-wrap gap-2">
                {s.renewal_mode === 'auto' && <button className="btn btn-ghost" onClick={() => setConfirm('period_end')}>إيقاف التجديد</button>}
                {s.withdrawal.eligible && <button className="btn btn-ghost" onClick={() => setConfirm('withdraw')}>العدول عن الاشتراك (خلال 7 أيام)</button>}
                {s.renewal_mode === 'link' && !s.withdrawal.eligible && <p className="muted text-sm">لا يوجد سحب تلقائي. إن لم تجدد بنفسك تنتهي العضوية آخر الدورة.</p>}
              </div>
            )}
            {s.status === 'cancel_at_period_end' && <p className="muted text-sm">طُلب الإيقاف في {dateAr(s.cancel_requested_at)}. تبقى عضويتك فعّالة حتى {dateAr(s.cycle_end)} ولن تُحصَّل دفعة جديدة.</p>}
            {confirm && !verification && (
              <div className="rounded-[var(--radius-md)] bg-paper-2 p-4 grid gap-3">
                <p className="text-sm">للتأكد أنك صاحب العضوية، نتحقق من جوالك المنتهي بـ <span className="num">{data.member.phone_last4}</span>.</p>
                <OtpStep cardToken={token} onVerified={(t) => setVerification(t)} label="تأكيد" />
                <button className="btn btn-ghost" onClick={() => setConfirm(null)}>تراجع</button>
              </div>
            )}
            {confirm === 'period_end' && verification && (
              <div className="rounded-[var(--radius-md)] bg-paper-2 p-4 grid gap-3">
                <p>تبقى عضويتك فعّالة حتى <b className="num">{dateAr(s.cycle_end)}</b> ثم تتوقف، ولن تُحصَّل أي دفعة جديدة. متأكد؟</p>
                <div className="flex gap-2"><button className="btn btn-primary" disabled={busy} onClick={() => cancel('period_end')}>نعم، أوقف التجديد</button><button className="btn btn-ghost" onClick={() => { setConfirm(null); setVerification(''); }}>تراجع</button></div>
              </div>
            )}
            {confirm === 'withdraw' && verification && (
              <div className="rounded-[var(--radius-md)] bg-paper-2 p-4 grid gap-3">
                <p>العدول يوقف العضوية فورًا ويُسترد لك <b className="num">{(s.withdrawal.refund_halalas / 100).toFixed(2)} ريال</b> (المدفوع بعد حسم {s.used} زيارة مستخدمة). متأكد؟</p>
                <div className="flex gap-2"><button className="btn btn-primary" disabled={busy} onClick={() => cancel('withdraw')}>نعم، أريد العدول</button><button className="btn btn-ghost" onClick={() => { setConfirm(null); setVerification(''); }}>تراجع</button></div>
              </div>
            )}
          </section>
        )}
        {!s && <section className="card p-5"><p>لا توجد عضوية فعّالة. <Link className="underline" to={`/s/${data.shop.slug}`}>اشترك من جديد</Link>.</p></section>}
        <p className="muted text-xs">هذه البطاقة خاصة برقم جوالك. لا تشاركها. للاستفسار تواصل مع المحل{data.shop.phone ? ` على ${data.shop.phone}` : ''}. <Link to="/me" className="underline">كل عضوياتي</Link></p>
      </div>
    </main>
  );
}
